import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where } from "firebase/firestore";
import { TOPICS } from "./mathTopics";
import MathInput, { latexToLogic, latexToDisplay } from "./MathInput";
import "./App.css";

// Solve ax² + bx + c = 0, returns [r1, r2] or null if no real roots
const solveQuadratic = (A, B, C) => {
  const disc = B * B - 4 * A * C;
  if (disc < 0) return null;
  const r1 = Math.round(((-B + Math.sqrt(disc)) / (2 * A)) * 100) / 100;
  const r2 = Math.round(((-B - Math.sqrt(disc)) / (2 * A)) * 100) / 100;
  return [r1, r2];
};

function App() {
  const [mode, setMode] = useState(null);
  const [session, setSession] = useState(null); // { classCode, studentName, classId }

  if (!mode) {
    return (
      <div className="page" style={{ textAlign: "center" }}>
        <h1 className="home-title">TKTeK Practice</h1>
        <p className="home-subtitle">Choose your mode to get started</p>
        <div className="home-buttons">
          <button className="home-btn student" onClick={() => setMode("individual")}>
            Individual Practice
          </button>
          <button className="home-btn teacher" style={{ backgroundColor: "#9c27b0" }} onClick={() => setMode("class")}>
            Join a Class
          </button>
          <button className="home-btn teacher" onClick={() => setMode("teacher")}>
            Teacher
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {mode === "individual" && <Student onBack={() => setMode(null)} session={null} />}
      {mode === "class" && !session && <JoinClass onBack={() => setMode(null)} onJoin={(s) => setSession(s)} />}
      {mode === "class" && session && <Student onBack={() => { setMode(null); setSession(null); }} session={session} />}
      {mode === "teacher" && <Teacher onBack={() => setMode(null)} />}
    </div>
  );
}

function JoinClass({ onBack, onJoin }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [step, setStep] = useState("code"); // "code" or "name"
  const [error, setError] = useState("");
  const [classDoc, setClassDoc] = useState(null);

  const handleCodeSubmit = async () => {
    setError("");
    const q = query(collection(db, "classes"), where("code", "==", code.toUpperCase().trim()));
    const snap = await getDocs(q);
    if (snap.empty) { setError("Class code not found."); return; }
    const cls = { id: snap.docs[0].id, ...snap.docs[0].data() };
    if (cls.status === "closed") { setError("This class is currently closed."); return; }
    setClassDoc(cls);
    setStep("name");
  };

  const handleJoin = () => {
    if (!name.trim()) { setError("Please enter your name."); return; }
    onJoin({ classCode: classDoc.code, studentName: name.trim(), classId: classDoc.id });
  };

  return (
    <div className="page">
      <button className="back-btn" onClick={onBack}>← Back to Menu</button>
      <h2>Join a Class</h2>

      {step === "code" && (
        <div className="login-box">
          <input
            placeholder="Enter class code"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleCodeSubmit()}
            style={{ textTransform: "uppercase" }}
          />
          <button className="btn btn-primary" onClick={handleCodeSubmit}>Continue</button>
          {error && <p className="error-msg">{error}</p>}
        </div>
      )}

      {step === "name" && (
        <div className="login-box">
          <p style={{ color: "#3b3b98", fontWeight: 600 }}>Joined: {classDoc.name}</p>
          <input
            placeholder="Enter your name"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleJoin()}
          />
          <button className="btn btn-primary" onClick={handleJoin}>Start Practicing</button>
          {error && <p className="error-msg">{error}</p>}
        </div>
      )}
    </div>
  );
}

function Teacher({ onBack }) {
  const [unlocked, setUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState(false);

  const handlePasswordSubmit = () => {
    if (passwordInput === "Vissers") {
      setUnlocked(true);
    } else {
      setPasswordError(true);
      setPasswordInput("");
    }
  };

  if (!unlocked) {
    return (
      <div className="page">
        <button className="back-btn" onClick={onBack}>← Back to Menu</button>
        <h2>Teacher Login</h2>
        <div className="login-box">
          <input
            type="password"
            value={passwordInput}
            onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(false); }}
            onKeyDown={(e) => e.key === "Enter" && handlePasswordSubmit()}
            placeholder="Enter password"
          />
          <button className="btn btn-primary" onClick={handlePasswordSubmit}>Enter</button>
          {passwordError && <p className="error-msg">Incorrect password.</p>}
        </div>
      </div>
    );
  }

  return <TeacherPanel onBack={onBack} />;
}

function TeacherPanel({ onBack }) {
  const [tab, setTab] = useState("create");

  return (
    <div className="page">
      <button className="back-btn" onClick={onBack}>← Back to Menu</button>
      <h2>Teacher</h2>

      <div className="tab-bar">
        <button className={`tab-btn ${tab === "create" ? "active" : ""}`} onClick={() => setTab("create")}>Create Question</button>
        <button className={`tab-btn ${tab === "saved" ? "active" : ""}`} onClick={() => setTab("saved")}>Question Bank</button>
        <button className={`tab-btn ${tab === "tests" ? "active" : ""}`} onClick={() => setTab("tests")}>Test Builder</button>
        <button className={`tab-btn ${tab === "classes" ? "active" : ""}`} onClick={() => setTab("classes")}>Classes</button>
      </div>

      {tab === "create" && <CreateQuestion />}
      {tab === "saved" && <SavedQuestions />}
      {tab === "tests" && <TestBuilder />}
      {tab === "classes" && <Classes />}
    </div>
  );
}

function CreateQuestion() {
  const [mode, setMode] = useState("topic"); // "topic" or "custom"

  return (
    <div>
      <div className="mode-toggle">
        <button
          className={`toggle-btn ${mode === "topic" ? "active" : ""}`}
          onClick={() => setMode("topic")}
        >
          Topic-Based
        </button>
        <button
          className={`toggle-btn ${mode === "custom" ? "active" : ""}`}
          onClick={() => setMode("custom")}
        >
          Custom
        </button>
      </div>

      {mode === "topic" ? <TopicQuestion /> : <CustomQuestion />}
    </div>
  );
}

function TopicQuestion() {
  const [topic, setTopic] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [rootMode, setRootMode] = useState("Integer Roots");
  const [rootMin, setRootMin] = useState(-10);
  const [rootMax, setRootMax] = useState(10);
  const [savedMessage, setSavedMessage] = useState("");
  const [preview, setPreview] = useState(null);

  const topicList = Object.keys(TOPICS);
  const questionTypes = topic ? Object.keys(TOPICS[topic]?.questionTypes || {}) : [];
  const modes = topic && questionType ? TOPICS[topic].questionTypes[questionType]?.modes || [] : [];

  const generatePreview = () => {
    if (!topic || !questionType) return;
    const result = TOPICS[topic].questionTypes[questionType].generate(rootMode, {
      rootMin, rootMax
    });
    setPreview(result);
  };

  const handleSave = async () => {
    if (!topic || !questionType) return;
    await addDoc(collection(db, "questions"), {
      questionCategory: "topic-based",
      topic,
      questionType,
      rootMode,
      rootMin,
      rootMax,
      createdAt: Date.now()
    });
    setSavedMessage("Question saved!");
    setPreview(null);
    setTimeout(() => setSavedMessage(""), 3000);
  };

  return (
    <div>
      <h4>Topic</h4>
      <div className="chip-row">
        {topicList.map((t) => (
          <button
            key={t}
            className={`chip ${topic === t ? "active" : ""}`}
            onClick={() => { setTopic(t); setQuestionType(""); setPreview(null); }}
          >
            {t}
          </button>
        ))}
      </div>

      {topic && (
        <>
          <h4>Question Type</h4>
          <div className="chip-row">
            {questionTypes.map((qt) => (
              <button
                key={qt}
                className={`chip ${questionType === qt ? "active" : ""}`}
                onClick={() => { setQuestionType(qt); setPreview(null); }}
              >
                {qt}
              </button>
            ))}
          </div>
        </>
      )}

      {questionType && modes.length > 0 && (
        <>
          <h4>Mode</h4>
          <div className="chip-row">
            {modes.map((m) => (
              <button
                key={m}
                className={`chip ${rootMode === m ? "active" : ""}`}
                onClick={() => { setRootMode(m); setPreview(null); }}
              >
                {m}
              </button>
            ))}
          </div>
        </>
      )}

      {questionType && rootMode === "Integer Roots" && (
        <>
          <h4>Root Range</h4>
          <div className="range-row">
            <input type="number" placeholder="Min" value={rootMin} onChange={(e) => setRootMin(Number(e.target.value))} />
            <input type="number" placeholder="Max" value={rootMax} onChange={(e) => setRootMax(Number(e.target.value))} />
          </div>
        </>
      )}

      {topic && questionType && (
        <button className="btn btn-secondary" style={{ marginTop: "16px" }} onClick={generatePreview}>
          Preview Question
        </button>
      )}

      {preview && (
        <div className="preview-box">
          <span className="preview-label">Preview</span>
          <p className="preview-question">{preview.display}</p>
          <p className="preview-answer">Roots: x = {preview.answers[0]}, x = {preview.answers[1]}</p>
        </div>
      )}

      <br />
      <button className="btn btn-primary" onClick={handleSave} disabled={!topic || !questionType}>
        Save Question
      </button>
      <p className="status-msg">{savedMessage}</p>
    </div>
  );
}

function CustomQuestion() {
  const [logic, setLogic] = useState("");
  const [display, setDisplay] = useState("");
  const [topic, setTopic] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const aMin = 1, aMax = 9, bMin = -10, bMax = 10;

  // Detect if the logic contains x (solve-for-x) or not (evaluate)
  const hasX = /\bx\b/.test(logic);

  const autoDisplay = (logicStr) => {
    return logicStr
      .replaceAll("**3", "³")
      .replaceAll("**2", "²")
      .replaceAll("*", "×")
      .replace(/(?<!\{)a(?!\})/g, "{a}")
      .replace(/(?<!\{)b(?!\})/g, "{b}");
  };

  const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  const preview = React.useMemo(() => {
    if (!display || !logic) return null;
    try {
      const a = randInt(aMin, aMax);
      const b = randInt(bMin, bMax);
      const previewDisplay = display.replaceAll("{a}", a).replaceAll("{b}", b);

      if (hasX) {
        // Replace coefficients, keep x symbolic, solve for x
        const withCoeffs = logic
          .replaceAll("×", "*").replaceAll("²", "**2").replaceAll("³", "**3")
          .replace(/\ba\b/g, a).replace(/\bb\b/g, b);
        // Try to extract ax² + bx + c pattern and solve
        // Use Function to evaluate with x as variable
        const fn = new Function("x", `return ${withCoeffs}`);
        // For quadratics: find roots numerically
        // Extract coefficients by evaluating at x=0,1,-1
        const c0 = fn(0);   // c
        const c1 = fn(1);   // a+b+c
        const cm1 = fn(-1); // a-b+c
        const A = (c1 + cm1 - 2 * c0) / 2;
        const B = (c1 - cm1) / 2;
        const C = c0;
        const roots = solveQuadratic(A, B, C);
        if (!roots) return null;
        return { previewDisplay, roots, answerType: "roots" };
      } else {
        const cleanLogic = logic
          .replaceAll("×", "*").replaceAll("²", "**2").replaceAll("³", "**3")
          .replace(/\ba\b/g, a).replace(/\bb\b/g, b);
        const answer = eval(cleanLogic);
        if (isNaN(answer)) return null;
        return { previewDisplay, answer, answerType: "single" };
      }
    } catch {
      return null;
    }
  }, [display, logic, aMin, aMax, bMin, bMax, hasX]);

  const handleSave = async () => {
    await addDoc(collection(db, "questions"), {
      questionCategory: "custom",
      logic,
      display,
      topic,
      answerType: hasX ? "roots" : "single",
      xMin: aMin,
      xMax: aMax,
      yMin: bMin,
      yMax: bMax,
      createdAt: Date.now()
    });
    setLogic(""); setDisplay(""); setTopic("");
    setSavedMessage("Question saved!");
    setTimeout(() => setSavedMessage(""), 3000);
  };

  return (
    <div>
      <div className="syntax-guide">
        <strong>Syntax guide</strong>
        <div className="syntax-grid">
          <span><code>a*x**2 + b*x</code> quadratic</span>
          <span><code>a*x + b</code> linear</span>
          <span><code>a*x**2 + b*x + 3</code> with constant</span>
          <span><code>a**2 + b**2</code> evaluate (no x)</span>
        </div>
        <p><code>a</code> and <code>b</code> are <strong>randomized coefficients</strong>. <code>x</code> is the variable students solve for.</p>
        {hasX && <p style={{ color: "#3b3b98", fontWeight: 600 }}>Solve-for-x mode — students will find the roots.</p>}
        {!hasX && logic && <p style={{ color: "#888" }}>Evaluate mode — students calculate the answer.</p>}
      </div>

      <h4>Topic</h4>
      <input placeholder="e.g. Factoring, Radicals" value={topic} onChange={(e) => setTopic(e.target.value)} />

      <h4>Enter your question</h4>
      <MathInput
        value={logic}
        onChange={(latex) => {
          setLogic(latexToLogic(latex));
          setDisplay(latexToDisplay(latex));
        }}
        placeholder="Type the question using math notation..."
      />

      {preview && (
        <div className="preview-box">
          <span className="preview-label">Preview</span>
          <p className="preview-question">{preview.previewDisplay}</p>
          {preview.answerType === "roots"
            ? <p className="preview-answer">Roots: x = {preview.roots[0]}, x = {preview.roots[1]}</p>
            : <p className="preview-answer">Answer: {preview.answer}</p>
          }
        </div>
      )}

      <br />
      <button className="btn btn-primary" onClick={handleSave}>Save Question</button>
      <p className="status-msg">{savedMessage}</p>
    </div>
  );
}

function SavedQuestions() {
  const [questions, setQuestions] = useState([]);
  const [statusMsg, setStatusMsg] = useState("");
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      const snapshot = await getDocs(collection(db, "questions"));
      setQuestions(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    };
    fetch();
  }, []);

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "questions", id));
    setQuestions(questions.filter((q) => q.id !== id));
    if (preview?.id === id) setPreview(null);
  };

  // Group questions by topic
  const grouped = questions.reduce((acc, q) => {
    const topic = q.topic || "Uncategorised";
    if (!acc[topic]) acc[topic] = [];
    acc[topic].push(q);
    return acc;
  }, {});

  const getLabel = (q) => {
    if (q.questionCategory === "topic-based") return `${q.questionType} — ${q.rootMode}`;
    if (q.questionCategory === "custom") return `Custom — ${q.topic || "No topic"}`;
    return "Custom question";
  };

  const generatePreview = (q) => {
    if (q.questionCategory === "topic-based") {
      const topicDef = TOPICS[q.topic];
      if (!topicDef) return;
      const qtDef = topicDef.questionTypes[q.questionType];
      if (!qtDef) return;
      const result = qtDef.generate(q.rootMode, { rootMin: q.rootMin, rootMax: q.rootMax });
      setPreview({ ...result, id: q.id });
    }
  };

  return (
    <div>
      <br />
      {questions.length === 0 ? (
        <p style={{ color: "#888" }}>No questions saved yet.</p>
      ) : (
        <>
          {Object.entries(grouped).map(([topic, qs]) => (
            <div key={topic} className="bank-topic">
              <button
                className="bank-topic-header"
                onClick={() => setExpandedTopic(expandedTopic === topic ? null : topic)}
              >
                <span>{topic}</span>
                <span className="bank-count">{qs.length} question{qs.length !== 1 ? "s" : ""}</span>
                <span>{expandedTopic === topic ? "▲" : "▼"}</span>
              </button>

              {expandedTopic === topic && (
                <div className="bank-topic-body">
                  {qs.map((q) => (
                    <div key={q.id} className="bank-item">
                      <div className="bank-item-info">
                        <p>{getLabel(q)}</p>
                      </div>
                      <div className="bank-item-actions">
                        <button
                          className="btn btn-secondary"
                          style={{ padding: "4px 10px", fontSize: "0.8rem" }}
                          onClick={() => generatePreview(q)}
                        >
                          Preview
                        </button>
                        <button
                          className="btn"
                          style={{ backgroundColor: "#e53935", color: "white", padding: "4px 10px", fontSize: "0.8rem" }}
                          onClick={() => handleDelete(q.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {preview && (
            <div className="preview-box" style={{ marginTop: 20 }}>
              <span className="preview-label">Question Preview</span>
              <p className="preview-question">{preview.display}</p>
              {preview.answers && (
                <p className="preview-answer">
                  {preview.answerType === "roots"
                    ? `Roots: x = ${preview.answers[0]}, x = ${preview.answers[1]}`
                    : `Factors: (x + ${preview.answers[0]})(x + ${preview.answers[1]})`}
                </p>
              )}
            </div>
          )}

          <br />
          <button
            className="btn"
            style={{ backgroundColor: "#e53935", color: "white" }}
            onClick={async () => {
              const snapshot = await getDocs(collection(db, "questions"));
              await Promise.all(snapshot.docs.map((d) => deleteDoc(doc(db, "questions", d.id))));
              setQuestions([]);
              setStatusMsg("All questions deleted.");
              setTimeout(() => setStatusMsg(""), 3000);
            }}
          >
            Clear All
          </button>
        </>
      )}
      <p className="status-msg">{statusMsg}</p>
    </div>
  );
}

function TestBuilder() {
  const [view, setView] = useState("list"); // "list" | "build" | "randomise"
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");

  useEffect(() => {
    const fetch = async () => {
      const snapshot = await getDocs(collection(db, "tests"));
      setTests(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    };
    fetch();
  }, []);

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "tests", id));
    setTests(tests.filter((t) => t.id !== id));
  };

  if (view === "build") return <BuildTest onBack={() => setView("list")} onSaved={(t) => { setTests([...tests, t]); setView("list"); }} />;
  if (view === "randomise" && selectedTest) return <RandomiseTest test={selectedTest} onBack={() => setView("list")} />;

  return (
    <div>
      <br />
      <button className="btn btn-primary" onClick={() => setView("build")}>+ New Test Sheet</button>

      <div style={{ marginTop: 20 }}>
        {tests.length === 0 && <p style={{ color: "#888" }}>No test sheets yet. Create one above.</p>}
        {tests.map((t) => (
          <div key={t.id} className="bank-item" style={{ marginBottom: 10 }}>
            <div className="bank-item-info">
              <p style={{ fontWeight: 600, margin: 0 }}>{t.name}</p>
              <small style={{ color: "#888" }}>{t.questions?.length || 0} question{t.questions?.length !== 1 ? "s" : ""}</small>
            </div>
            <div className="bank-item-actions">
              <button className="btn btn-orange" style={{ padding: "4px 12px", fontSize: "0.85rem" }}
                onClick={() => { setSelectedTest(t); setView("randomise"); }}>
                Randomise
              </button>
              <button className="btn" style={{ backgroundColor: "#e53935", color: "white", padding: "4px 10px", fontSize: "0.8rem" }}
                onClick={() => handleDelete(t.id)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="status-msg">{statusMsg}</p>
    </div>
  );
}

function BuildTest({ onBack, onSaved }) {
  const [testName, setTestName] = useState("");
  const [questions, setQuestions] = useState([]); // questions added to this test
  const [topic, setTopic] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [rootMode, setRootMode] = useState("Integer Roots");
  const [rootMin, setRootMin] = useState(-10);
  const [rootMax, setRootMax] = useState(10);
  const [statusMsg, setStatusMsg] = useState("");

  const topicList = Object.keys(TOPICS);
  const questionTypes = topic ? Object.keys(TOPICS[topic]?.questionTypes || {}) : [];
  const modes = topic && questionType ? TOPICS[topic].questionTypes[questionType]?.modes || [] : [];

  const addQuestion = () => {
    if (!topic || !questionType) return;
    const q = { topic, questionType, rootMode, rootMin, rootMax, questionCategory: "topic-based" };
    setQuestions([...questions, q]);
    setStatusMsg("Question added.");
    setTimeout(() => setStatusMsg(""), 2000);
  };

  const removeQuestion = (i) => setQuestions(questions.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!testName || questions.length === 0) return;
    const docRef = await addDoc(collection(db, "tests"), {
      name: testName,
      questions,
      createdAt: Date.now()
    });
    onSaved({ id: docRef.id, name: testName, questions });
  };

  return (
    <div>
      <button className="back-btn" onClick={onBack}>← Back to Tests</button>
      <h3>New Test Sheet</h3>

      <h4>Test Name</h4>
      <input placeholder="e.g. Precalc 11 Final Exam" value={testName} onChange={(e) => setTestName(e.target.value)} />

      <h4 style={{ marginTop: 20 }}>Add Questions</h4>
      <h4>Topic</h4>
      <div className="chip-row">
        {topicList.map((t) => (
          <button key={t} className={`chip ${topic === t ? "active" : ""}`}
            onClick={() => { setTopic(t); setQuestionType(""); }}>
            {t}
          </button>
        ))}
      </div>

      {topic && (
        <>
          <h4>Question Type</h4>
          <div className="chip-row">
            {questionTypes.map((qt) => (
              <button key={qt} className={`chip ${questionType === qt ? "active" : ""}`}
                onClick={() => setQuestionType(qt)}>
                {qt}
              </button>
            ))}
          </div>
        </>
      )}

      {questionType && modes.length > 0 && (
        <>
          <h4>Mode</h4>
          <div className="chip-row">
            {modes.map((m) => (
              <button key={m} className={`chip ${rootMode === m ? "active" : ""}`}
                onClick={() => setRootMode(m)}>
                {m}
              </button>
            ))}
          </div>
        </>
      )}

      {questionType && rootMode === "Integer Roots" && (
        <>
          <h4>Root Range</h4>
          <div className="range-row">
            <input type="number" placeholder="Min" value={rootMin} onChange={(e) => setRootMin(Number(e.target.value))} />
            <input type="number" placeholder="Max" value={rootMax} onChange={(e) => setRootMax(Number(e.target.value))} />
          </div>
        </>
      )}

      {topic && questionType && (
        <button className="btn btn-success" style={{ marginTop: 12 }} onClick={addQuestion}>
          + Add to Test
        </button>
      )}

      <p className="status-msg">{statusMsg}</p>

      {questions.length > 0 && (
        <>
          <h4 style={{ marginTop: 20 }}>Questions in this test ({questions.length})</h4>
          {questions.map((q, i) => (
            <div key={i} className="bank-item">
              <div className="bank-item-info">
                <p style={{ margin: 0 }}>{i + 1}. {q.topic} — {q.questionType} ({q.rootMode})</p>
              </div>
              <button className="btn" style={{ backgroundColor: "#e53935", color: "white", padding: "4px 10px", fontSize: "0.8rem" }}
                onClick={() => removeQuestion(i)}>
                Remove
              </button>
            </div>
          ))}
          <br />
          <button className="btn btn-primary" onClick={handleSave} disabled={!testName}>
            Save Test Sheet
          </button>
        </>
      )}
    </div>
  );
}

function RandomiseTest({ test, onBack }) {
  const [copies, setCopies] = useState(1);
  const [showAnswers, setShowAnswers] = useState(false);
  const [generated, setGenerated] = useState([]);

  const randomise = () => {
    const allCopies = [];
    for (let c = 0; c < copies; c++) {
      const questions = test.questions.map((q, i) => {
        const topicDef = TOPICS[q.topic];
        if (!topicDef) return null;
        const qtDef = topicDef.questionTypes[q.questionType];
        if (!qtDef) return null;
        return { num: i + 1, ...qtDef.generate(q.rootMode, { rootMin: q.rootMin, rootMax: q.rootMax }) };
      }).filter(Boolean);
      allCopies.push(questions);
    }
    setGenerated(allCopies);
  };

  return (
    <div>
      <button className="back-btn" onClick={onBack}>← Back to Tests</button>
      <h3>{test.name}</h3>
      <p style={{ color: "#888", fontSize: "0.9rem" }}>{test.questions.length} question{test.questions.length !== 1 ? "s" : ""}</p>

      <h4>Number of Copies</h4>
      <div className="range-row" style={{ maxWidth: 200 }}>
        <input type="number" min="1" max="35" value={copies} onChange={(e) => setCopies(Number(e.target.value))} />
      </div>

      <div style={{ marginTop: 12 }}>
        <button className="btn btn-orange" onClick={randomise}>Generate Copies</button>
        {generated.length > 0 && (
          <>
            <button className="btn btn-secondary" onClick={() => setShowAnswers(!showAnswers)}>
              {showAnswers ? "Hide Answers" : "Show Answers"}
            </button>
            <button className="btn btn-primary" onClick={() => window.print()}>Print</button>
          </>
        )}
      </div>

      <div style={{ marginTop: 24 }}>
        {generated.map((copy, ci) => (
          <div key={ci} className="test-copy">
            <div className="test-copy-header">
              <span>{test.name}</span>
              <span>Copy {ci + 1}</span>
              <span>Name: _______________</span>
            </div>
            {copy.map((q) => (
              <div key={q.num} className="question-item">
                <p>{q.num}. {q.display}</p>
                {showAnswers && q.answers && (
                  <small style={{ color: "#4caf50" }}>
                    {q.answerType === "roots"
                      ? `x = ${q.answers[0]}, x = ${q.answers[1]}`
                      : `(x + ${q.answers[0]})(x + ${q.answers[1]})`}
                  </small>
                )}
              </div>
            ))}
            <div style={{ borderTop: "1px dashed #ddd", marginTop: 24, marginBottom: 24 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

const generateCode = () => Math.random().toString(36).substring(2, 8).toUpperCase();

function Classes() {
  const [classes, setClasses] = useState([]);
  const [newClassName, setNewClassName] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [selectedClass, setSelectedClass] = useState(null);

  useEffect(() => { fetchClasses(); }, []);

  const fetchClasses = async () => {
    const snap = await getDocs(collection(db, "classes"));
    setClasses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const handleCreate = async () => {
    if (!newClassName.trim()) return;
    const code = generateCode();
    await addDoc(collection(db, "classes"), {
      name: newClassName.trim(),
      code,
      status: "open",
      createdAt: Date.now()
    });
    setNewClassName("");
    setStatusMsg(`Class created! Code: ${code}`);
    setTimeout(() => setStatusMsg(""), 5000);
    fetchClasses();
  };

  const toggleStatus = async (cls) => {
    const newStatus = cls.status === "open" ? "closed" : "open";
    await updateDoc(doc(db, "classes", cls.id), { status: newStatus });
    fetchClasses();
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "classes", id));
    fetchClasses();
  };

  if (selectedClass) {
    return <ClassResults cls={selectedClass} onBack={() => setSelectedClass(null)} />;
  }

  return (
    <div>
      <div className="login-box" style={{ flexDirection: "row", gap: 8, marginTop: 16 }}>
        <input
          placeholder="Class name e.g. Precalc 11 Block A"
          value={newClassName}
          onChange={(e) => setNewClassName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          style={{ flex: 1 }}
        />
        <button className="btn btn-primary" onClick={handleCreate}>Create</button>
      </div>
      <p className="status-msg">{statusMsg}</p>

      {classes.length === 0 ? (
        <p style={{ color: "#888" }}>No classes yet.</p>
      ) : (
        classes.map((cls) => (
          <div key={cls.id} className="saved-item" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <p style={{ margin: 0, fontWeight: 600 }}>{cls.name}</p>
              <small style={{ color: "#888" }}>Code: <strong style={{ color: "#3b3b98" }}>{cls.code}</strong> · {cls.status === "open" ? "🟢 Open" : "🔴 Closed"}</small>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-secondary" style={{ fontSize: "0.8rem", padding: "4px 10px" }} onClick={() => setSelectedClass(cls)}>Results</button>
              <button className="btn" style={{ fontSize: "0.8rem", padding: "4px 10px", backgroundColor: cls.status === "open" ? "#ff9800" : "#4caf50", color: "white" }} onClick={() => toggleStatus(cls)}>
                {cls.status === "open" ? "Close" : "Open"}
              </button>
              <button className="btn" style={{ fontSize: "0.8rem", padding: "4px 10px", backgroundColor: "#e53935", color: "white" }} onClick={() => handleDelete(cls.id)}>Delete</button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function ClassResults({ cls, onBack }) {
  const [attempts, setAttempts] = useState([]);
  const [view, setView] = useState("students"); // "students" | "questions" | "topics"

  useEffect(() => {
    const fetch = async () => {
      const q = query(collection(db, "attempts"), where("classCode", "==", cls.code));
      const snap = await getDocs(q);
      setAttempts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };
    fetch();
  }, [cls.code]);

  // Group by student
  const byStudent = attempts.reduce((acc, a) => {
    if (!acc[a.studentName]) acc[a.studentName] = { correct: 0, total: 0 };
    acc[a.studentName].total++;
    if (a.correct) acc[a.studentName].correct++;
    return acc;
  }, {});

  // Group by topic
  const byTopic = attempts.reduce((acc, a) => {
    const key = a.topic || "Custom";
    if (!acc[key]) acc[key] = { correct: 0, total: 0 };
    acc[key].total++;
    if (a.correct) acc[key].correct++;
    return acc;
  }, {});

  // Group by question display
  const byQuestion = attempts.reduce((acc, a) => {
    const key = a.questionDisplay || a.questionId;
    if (!acc[key]) acc[key] = { correct: 0, total: 0 };
    acc[key].total++;
    if (a.correct) acc[key].correct++;
    return acc;
  }, {});

  const pct = (c, t) => t === 0 ? "—" : `${Math.round((c / t) * 100)}%`;

  return (
    <div>
      <button className="back-btn" onClick={onBack}>← Back to Classes</button>
      <h3 style={{ margin: "12px 0 4px" }}>{cls.name}</h3>
      <p style={{ color: "#888", fontSize: "0.85rem", margin: "0 0 16px" }}>Code: {cls.code} · {attempts.length} attempts</p>

      <div className="tab-bar">
        <button className={`tab-btn ${view === "students" ? "active" : ""}`} onClick={() => setView("students")}>Students</button>
        <button className={`tab-btn ${view === "topics" ? "active" : ""}`} onClick={() => setView("topics")}>By Topic</button>
        <button className={`tab-btn ${view === "questions" ? "active" : ""}`} onClick={() => setView("questions")}>By Question</button>
      </div>

      {view === "students" && (
        <div style={{ marginTop: 12 }}>
          {Object.keys(byStudent).length === 0 ? <p style={{ color: "#888" }}>No attempts yet.</p> : (
            Object.entries(byStudent).map(([name, s]) => (
              <div key={name} className="saved-item" style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>{name}</span>
                <span style={{ color: s.correct / s.total >= 0.7 ? "#4caf50" : "#e53935" }}>
                  {s.correct}/{s.total} ({pct(s.correct, s.total)})
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {view === "topics" && (
        <div style={{ marginTop: 12 }}>
          {Object.keys(byTopic).length === 0 ? <p style={{ color: "#888" }}>No attempts yet.</p> : (
            Object.entries(byTopic).map(([topic, s]) => (
              <div key={topic} className="saved-item" style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>{topic}</span>
                <span style={{ color: s.correct / s.total >= 0.7 ? "#4caf50" : "#e53935" }}>
                  {s.correct}/{s.total} ({pct(s.correct, s.total)})
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {view === "questions" && (
        <div style={{ marginTop: 12 }}>
          {Object.keys(byQuestion).length === 0 ? <p style={{ color: "#888" }}>No attempts yet.</p> : (
            Object.entries(byQuestion).map(([q, s]) => (
              <div key={q} className="saved-item" style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: "0.9rem" }}>{q}</span>
                <span style={{ whiteSpace: "nowrap", color: s.correct / s.total >= 0.7 ? "#4caf50" : "#e53935" }}>
                  {s.correct}/{s.total} ({pct(s.correct, s.total)})
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function Student({ onBack, session }) {
  const [stage, setStage] = useState("topic"); // "topic" | "type" | "practice"
  const [topic, setTopic] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answer, setAnswer] = useState("");
  const [answer2, setAnswer2] = useState("");
  const [answer3, setAnswer3] = useState("");
  const [answer4, setAnswer4] = useState("");
  const [feedback, setFeedback] = useState("");
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const topicList = Object.keys(TOPICS);

  const generateQuestion = (t, qt) => {
    const qtDef = TOPICS[t].questionTypes[qt];
    const result = qtDef.generate("Integer Roots", { rootMin: -10, rootMax: 10 });
    setCurrentQuestion({ ...result, topic: t, questionType: qt });
    setAnswer(""); setAnswer2(""); setAnswer3(""); setAnswer4(""); setFeedback("");
  };

  const selectTopic = (t) => {
    setTopic(t);
    setQuestionType("");
    setCurrentQuestion(null);
    setScore({ correct: 0, total: 0 });
    setStage("type");
  };

  const selectType = (qt) => {
    setQuestionType(qt);
    setScore({ correct: 0, total: 0 });
    setStage("practice");
    generateQuestion(topic, qt);
  };

  const logAttempt = (correct) => {
    if (!session) return;
    addDoc(collection(db, "attempts"), {
      classCode: session.classCode,
      studentName: session.studentName,
      questionId: `${topic}-${questionType}`,
      questionDisplay: currentQuestion.display,
      topic,
      questionType,
      correct,
      timestamp: Date.now()
    });
  };

  const checkAnswer = () => {
    const { answerType, answers, hint, p, q, r, s, gcf, a: ca, b: cb } = currentQuestion;
    let correct = false;

    if (answerType === "roots" || answerType === "factors") {
      const [ea, eb] = answers;
      const v1 = Number(answer), v2 = Number(answer2);
      correct = (v1 === ea && v2 === eb) || (v1 === eb && v2 === ea);
      logAttempt(correct);
      if (correct) { setFeedback("✅ Correct!"); }
      else {
        const m1 = v1 === ea || v1 === eb;
        const m2 = v2 === ea || v2 === eb;
        if (m1 && !m2) setFeedback("First value is correct — check the second.");
        else if (!m1 && m2) setFeedback("Second value is correct — check the first.");
        else if (hint) setFeedback(`❌ Not quite. Hint: ${hint}`);
        else setFeedback("❌ Not quite. Check your working.");
      }
    } else if (answerType === "leading-factors") {
      const v1 = Number(answer), v2 = Number(answer2), v3 = Number(answer3), v4 = Number(answer4);
      correct = (v1 === p && v2 === q && v3 === r && v4 === s) ||
                (v1 === r && v2 === s && v3 === p && v4 === q);
      logAttempt(correct);
      if (correct) { setFeedback("✅ Correct!"); }
      else if (hint) setFeedback(`❌ Not quite. Hint: ${hint}`);
      else setFeedback("❌ Not quite. Check your working.");
    } else if (answerType === "common-factor") {
      const vGcf = Number(answer), vA = Number(answer2), vB = Number(answer3);
      correct = vGcf === gcf && vA === ca && vB === cb;
      logAttempt(correct);
      if (correct) { setFeedback("✅ Correct!"); }
      else if (vGcf !== gcf) setFeedback("❌ Check the common factor — what divides both terms?");
      else if (hint) setFeedback(`❌ GCF is right — check what's left inside the bracket. Hint: ${hint}`);
      else setFeedback("❌ Not quite. Check your working.");
    } else {
      const num = Number(answer);
      correct = num === currentQuestion.correctAnswer;
      logAttempt(correct);
      if (correct) setFeedback("✅ Correct!");
      else if (num < currentQuestion.correctAnswer) setFeedback("⬆️ Too low");
      else setFeedback("⬇️ Too high");
    }

    setScore((s) => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));
  };

  const sessionBanner = session && (
    <div className="session-banner">👤 {session.studentName} · Class: {session.classCode}</div>
  );

  // ── Stage: pick a topic ──
  if (stage === "topic") {
    return (
      <div className="page">
        <button className="back-btn" onClick={onBack}>← Back to Menu</button>
        <h2>Practice</h2>
        {sessionBanner}
        <p style={{ color: "#888", marginBottom: 20 }}>What do you want to practise?</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {topicList.map((t) => (
            <button key={t} className="home-btn student" style={{ textAlign: "left", fontSize: "1rem" }} onClick={() => selectTopic(t)}>
              {t}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Stage: pick a question type ──
  if (stage === "type") {
    const questionTypes = Object.keys(TOPICS[topic].questionTypes);
    return (
      <div className="page">
        <button className="back-btn" onClick={() => setStage("topic")}>← Back to Topics</button>
        <h2>{topic}</h2>
        {sessionBanner}
        <p style={{ color: "#888", marginBottom: 20 }}>Choose a question type:</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {questionTypes.map((qt) => (
            <button key={qt} className="home-btn student" style={{ textAlign: "left", fontSize: "1rem" }} onClick={() => selectType(qt)}>
              {TOPICS[topic].questionTypes[qt].label || qt}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Stage: practice ──
  if (!currentQuestion) return <div className="page"><p>Loading...</p></div>;

  const { answerType } = currentQuestion;

  return (
    <div className="page">
      <button className="back-btn" onClick={() => setStage("type")}>← Back</button>
      <h2>Practice</h2>
      {sessionBanner}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <p style={{ color: "#888", fontSize: "0.85rem", margin: 0 }}>{topic} — {questionType}</p>
        <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#3b3b98" }}>{score.correct}/{score.total}</span>
      </div>
      <p className="question-text">{currentQuestion.display}</p>

      {answerType === "roots" && (
        <div className="roots-row">
          <div className="root-input"><label>x =</label>
            <input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="First root" />
          </div>
          <div className="root-input"><label>x =</label>
            <input value={answer2} onChange={(e) => setAnswer2(e.target.value)} placeholder="Second root" onKeyDown={(e) => e.key === "Enter" && checkAnswer()} />
          </div>
        </div>
      )}

      {answerType === "factors" && (
        <div className="roots-row">
          <div className="root-input"><label>(x +</label>
            <input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="?" />
            <label>)</label>
          </div>
          <div className="root-input"><label>(x +</label>
            <input value={answer2} onChange={(e) => setAnswer2(e.target.value)} placeholder="?" onKeyDown={(e) => e.key === "Enter" && checkAnswer()} />
            <label>)</label>
          </div>
        </div>
      )}

      {answerType === "leading-factors" && (
        <div>
          <p style={{ fontSize: "0.85rem", color: "#888", margin: "0 0 8px" }}>Enter your answer as (px + q)(rx + s)</p>
          <div className="roots-row">
            <div className="root-input"><label>(</label>
              <input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="p" style={{ width: 50 }} />
              <label>x +</label>
              <input value={answer2} onChange={(e) => setAnswer2(e.target.value)} placeholder="q" style={{ width: 50 }} />
              <label>)</label>
            </div>
            <div className="root-input"><label>(</label>
              <input value={answer3} onChange={(e) => setAnswer3(e.target.value)} placeholder="r" style={{ width: 50 }} />
              <label>x +</label>
              <input value={answer4} onChange={(e) => setAnswer4(e.target.value)} placeholder="s" onKeyDown={(e) => e.key === "Enter" && checkAnswer()} style={{ width: 50 }} />
              <label>)</label>
            </div>
          </div>
        </div>
      )}

      {answerType === "common-factor" && (
        <div>
          <p style={{ fontSize: "0.85rem", color: "#888", margin: "0 0 8px" }}>Enter: GCF · x(ax + b)</p>
          <div className="roots-row">
            <div className="root-input">
              <label>GCF =</label>
              <input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="?" style={{ width: 60 }} />
            </div>
            <div className="root-input"><label>x(</label>
              <input value={answer2} onChange={(e) => setAnswer2(e.target.value)} placeholder="a" style={{ width: 50 }} />
              <label>x +</label>
              <input value={answer3} onChange={(e) => setAnswer3(e.target.value)} placeholder="b" onKeyDown={(e) => e.key === "Enter" && checkAnswer()} style={{ width: 50 }} />
              <label>)</label>
            </div>
          </div>
        </div>
      )}

      {answerType === "single" && (
        <input value={answer} onChange={(e) => setAnswer(e.target.value)} onKeyDown={(e) => e.key === "Enter" && checkAnswer()} placeholder="Your answer" />
      )}

      <div>
        <button className="btn btn-success" onClick={checkAnswer}>Submit</button>
        <button className="btn btn-secondary" onClick={() => generateQuestion(topic, questionType)}>Next Question</button>
      </div>
      <p className="feedback">{feedback}</p>
    </div>
  );
}

export default App;
